import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, MapPin, User, Calendar } from 'react-feather';
import toast from 'react-hot-toast';
import { buildApiErrorMessage, getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';

const AuthorizationPage = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const action = searchParams.get('action'); // 'approve' or 'decline'
  
  // Debug logging
  console.log('URL action parameter:', action);
  console.log('All search params:', Object.fromEntries(searchParams.entries()));
  console.log('Current URL:', window.location.href);
  console.log('Action type:', typeof action);
  console.log('Action === "approve":', action === 'approve');
  console.log('Action === "decline":', action === 'decline');
  
  // Default to 'approve' if no action parameter is provided
  const effectiveAction = action || 'approve';
  console.log('Effective action:', effectiveAction);
  
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${apiBaseUrl}/api/public/jobs/${jobId}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Job data loaded:', result.data);
          setJobData(result.data);
        } else {
          console.error('API returned error:', result);
          toast.error(result.message || 'Failed to load job data');
          navigate('/auth/login');
        }
      } else {
        toast.error('Job not found or access denied');
        navigate('/auth/login');
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      console.error('Error details:', error.message);
      toast.error('Error loading job details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorization = async (approved) => {
    setProcessing(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${apiBaseUrl}/api/public/jobs/${jobId}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ approved }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Check if already authorized
          if (result.alreadyAuthorized) {
            toast.success(result.message);
          } else if (approved) {
            toast.success('Appointment authorized successfully!');
          } else {
            toast.success('Appointment declined successfully!');
          }
        } else {
          toast.error(result.message || 'Failed to process authorization');
        }
        
        // Redirect to the specific job after a short delay
        setTimeout(() => {
          navigate(`/appointments/${jobId}`);
        }, 2000);
      } else {
        let errBody = null;
        try {
          errBody = await response.json();
        } catch {
          /* ignore */
        }
        toast.error(buildApiErrorMessage(errBody, response.status));
      }
    } catch (error) {
      console.error('Error processing authorization:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Error processing authorization'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h1>
          <p className="text-gray-600 mb-4">The requested job could not be found or you don't have access to it.</p>
          <button
            onClick={() => navigate('/auth/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      // Extract date parts to avoid timezone conversion
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      if (isNaN(localDate.getTime())) {
        return 'Invalid Date';
      }
      
      return localDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const formatTime = (timeString) => {
    try {
      // Handle both "04:01:00" and "04:01 AM" formats
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0);
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }
      return timeString;
    } catch (error) {
      console.error('Time formatting error:', error);
      return 'Invalid Time';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {effectiveAction === 'approve' ? 'Authorize Follow-up Appointment' : 'Decline Follow-up Appointment'}
                </h1>
                <p className="text-blue-100">
                  {effectiveAction === 'approve' 
                    ? 'Please review the details below and confirm your authorization for this follow-up appointment.'
                    : 'Are you sure you want to decline this follow-up appointment?'
                  }
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">{jobData.job_number}</div>
                <div className="text-blue-100">Job Number</div>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="px-6 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Basic Info */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Details</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-blue-600 mt-1 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Date & Time</div>
                        <div className="text-gray-600">
                          {formatDate(jobData.scheduled_date)} at {formatTime(jobData.scheduled_time)}
                          <br />
                          <small className="text-gray-400">Raw: {jobData.scheduled_date} | {jobData.scheduled_time}</small>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-blue-600 mt-1 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Duration</div>
                        <div className="text-gray-600">
                          {jobData.estimated_duration_minutes} minutes
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-blue-600 mt-1 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Location</div>
                        <div className="text-gray-600">
                          {jobData.location_address}<br />
                          {jobData.location_city}, {jobData.location_state} {jobData.location_zip_code}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <User className="h-5 w-5 text-blue-600 mt-1 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Interpreter</div>
                        <div className="text-gray-600">
                          {jobData.interpreter_first_name} {jobData.interpreter_last_name}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Description & Notes */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      <div>
                        <div className="font-medium text-gray-900">Service Type</div>
                        <div className="text-gray-600">{jobData.service_type_name}</div>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Languages</div>
                        <div className="text-gray-600">
                          {jobData.source_language_name} → {jobData.target_language_name}
                        </div>
                      </div>
                      {jobData.description && (
                        <div>
                          <div className="font-medium text-gray-900">Description</div>
                          <div className="text-gray-600">{jobData.description}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Follow-up Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-blue-600 mt-1 mr-3" />
                    <div>
                      <div className="font-medium text-blue-900">Follow-up Appointment</div>
                      <div className="text-blue-700 text-sm mt-1">
                        This is a follow-up appointment requested by your interpreter. 
                        Please review the details and authorize if you wish to proceed.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {effectiveAction === 'approve' ? (
                  <>
                    <button
                      onClick={() => handleAuthorization(true)}
                      disabled={processing}
                      className="flex items-center justify-center px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {processing ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      ) : (
                        <CheckCircle className="h-5 w-5 mr-2" />
                      )}
                      {processing ? 'Authorizing...' : 'Authorize Appointment'}
                    </button>
                    <button
                      onClick={() => handleAuthorization(false)}
                      disabled={processing}
                      className="flex items-center justify-center px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <XCircle className="h-5 w-5 mr-2" />
                      Decline Appointment
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleAuthorization(false)}
                      disabled={processing}
                      className="flex items-center justify-center px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {processing ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      ) : (
                        <XCircle className="h-5 w-5 mr-2" />
                      )}
                      {processing ? 'Declining...' : 'Confirm Decline'}
                    </button>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="flex items-center justify-center px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizationPage;
